import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Onboarding } from "../../../lib/apiTyped";
import { useApiCall } from "../../../lib/useToastError";

type Props = {
  orgId: string;
  displayName: string;
  designPartner: boolean;
  onBack: () => void;
};

const CONFETTI_COLORS = ["#0F0F0F", "#C8A951", "#D8D3F0", "#F5EDE0"];

export function StepDone({ orgId, displayName, designPartner, onBack }: Props) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { run } = useApiCall();

  const firstName = displayName.split(/\s+/)[0] || displayName;

  async function finish() {
    if (busy) return;
    setBusy(true);
    try {
      const confetti = (await import("canvas-confetti")).default;
      confetti({
        particleCount: 90,
        spread: 75,
        origin: { y: 0.4 },
        colors: CONFETTI_COLORS,
        ticks: 220,
        scalar: 0.95,
      });
      // Second smaller burst from the sides for a richer feel.
      setTimeout(() => {
        confetti({
          particleCount: 40,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: CONFETTI_COLORS,
        });
        confetti({
          particleCount: 40,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: CONFETTI_COLORS,
        });
      }, 180);
    } catch {
      /* ignore — confetti is purely decorative */
    }
    const result = await run(() => Onboarding.complete(orgId));
    if (!result) {
      setBusy(false);
      return;
    }
    setTimeout(() => navigate("/", { replace: true }), 700);
  }

  return (
    <section className="space-y-10 text-center sm:text-left">
      <div className="space-y-4">
        {designPartner && (
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-ink"
            style={{
              backgroundColor: "#F5EDE0",
              border: "1px solid rgba(200,169,81,0.35)",
            }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Design partner
          </span>
        )}
        <h1 className="text-5xl sm:text-7xl font-black tracking-tight text-ink leading-[1.05]">
          Todo listo
          {firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="text-base sm:text-lg text-ink-2 max-w-xl">
          Bienvenido a Cowboy. A partir de ahora cada factura, plan y miembro
          que añadas vivirá aquí.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4 justify-center sm:justify-start">
        <button
          type="button"
          onClick={finish}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-ink text-white text-lg font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:scale-[1.04] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {busy ? "Lanzando…" : "¡Empezar!"}
        </button>
        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          className="inline-flex items-center gap-1 text-sm text-ink-2 hover:text-ink transition-colors disabled:opacity-40"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Atrás
        </button>
      </div>
    </section>
  );
}
